import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { colors, distances } from '../../../styles/constants';
import Modal from '../../../components/Modal';
import { H2 } from '../../../components/Typography';
import { Button, ButtonGroup } from '../../../components/Buttons';
import { UiState } from '../types';
import { LoadingOverlay } from '../../../components/Loading';
import { Table, Thead, Tr, Th, Td, Tbody } from '../../../components/Table';

import { Order } from '../../../types/orders';
import { getSignificationAuthorizationGroupsForCapture, getCaptureableItems, getOrderItem } from '../../helpers';
import { AuthorizationGroup } from '../../types';
import { Money } from '../../../components/Formatters';

export interface SelectGroupProps {
    state: UiState;
    order: Order;
    setGroup: (order: Order, authorizationGroup: AuthorizationGroup) => void;
    dismissModal: () => void;
}

const getAuthorizationName = (group: AuthorizationGroup) => {
    if (group.authorization.payment_details && group.authorization.payment_details.payment_id) {
        return group.authorization.payment_details.payment_id;
    }
    return group.authorization.id || '';
};

const SelectGroup = (
    {
        state,
        order,
        setGroup,
        dismissModal,
    }: SelectGroupProps
) => {
    const groups = getSignificationAuthorizationGroupsForCapture(order);
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
    const [t] = useTranslation();
    return (
        <Modal width="70%">
            <H2>{t('orders.capture.select.title')}</H2>
            <InfoBox>{t('orders.capture.select.description')}</InfoBox>
            <Groups>
                {groups.map((group, i) => (
                    <Group
                        key={i}
                        className={i === selectedGroupIndex ? 'active' : ''}
                        onClick={() => setSelectedGroupIndex(i)}
                    >
                        <h3>{t('orders.capture.select.authorization_title', { name: getAuthorizationName(group) })}</h3>
                        <Table>
                            <Thead>
                                <Tr>
                                    <Th>{t('orders.capture.table.line_id')}</Th>
                                    <Th>{t('orders.capture.table.description')}</Th>
                                    <Th>{t('orders.capture.table.details')}</Th>
                                    <Th>{t('orders.capture.table.quantity')}</Th>
                                    <Th className="align-right">{t('orders.capture.table.capturable_amount')}</Th>
                                    <Th className="align-right">{t('orders.capture.table.authorized_amount')}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {getCaptureableItems(group).map((item, i) => (
                                    <Tr>
                                        <Td>{item.line_id}</Td>
                                        <Td>{item.description || '-'}</Td>
                                        <Td>{item.description_alias || '-'}</Td>
                                        <Td>{item.quantity || '-'}</Td>
                                        <Td className="align-right">
                                            <Money
                                                amount={item.net_amount || 0}
                                                currency={order.currency}
                                                boldCurrency={true}
                                            />
                                        </Td>
                                        <Td className="align-right">
                                            <Money
                                                amount={getOrderItem(order, item.line_id || -1).net_amount || 0}
                                                currency={order.currency}
                                                boldCurrency={true}
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Group>
                ))}
            </Groups>
            <ButtonGroup>
                <OperationButton onClick={() => selectedGroupIndex > -1 && setGroup(order, groups[selectedGroupIndex])}>
                    {t('orders.capture.select.select_group_button')}
                </OperationButton>
                <Button className="alt" onClick={dismissModal}>
                    {t('orders.capture.select.cancel_button')}
                </Button>
            </ButtonGroup>
            <Wrapper>{state === UiState.InFlight && <LoadingOverlay />}</Wrapper>
        </Modal>
    );
};

export default SelectGroup;

const Wrapper = styled.div`
    position: relative;
`;

const InfoBox = styled.div`
    position: relative;
    /* border: 2px solid ${colors.borderLight}; */
    border-left: 4px solid ${colors.borderDark};
    padding: 16px;
    margin-top: 16px;
    margin-bottom: 16px;
    line-height: 1.2em;
`;

const Groups = styled.div`
    margin-bottom: ${distances.large};
`;

const Group = styled.div`
    border: 2px solid #efefef;
    border-radius: 4px;
    padding: ${distances.normal};
    margin-bottom: ${distances.small};
    cursor: pointer;

    &:hover {
        background: ${colors.interactiveBackgroundHover};
    }

    &:last-child {
        margin-bottom: 0px;
    }
    &.active {
        border-color: ${colors.primary};
    }
`;

const OperationButton = styled(Button)`
    min-width: 220px;
`;
